import { useQueries, useQuery } from "@tanstack/react-query";
import { backendService } from "../services/backend.service";
import {
	type CallOffReplacementAdvice,
	type CallOffReplacementHint,
	callOffReplacementAdvice,
	type ReplacementRecommendation,
} from "../utils/callOffReplacementAdvice";
import type { CallOffReplacementGateResponse } from "../utils/models";

// Module-level so `select` keeps one identity and TanStack re-runs it only when the data changes. A throw
// here (an outcome the dialog has no advice for) becomes the query's error, so it surfaces as `isGateError`.
const toAdvice = (data: CallOffReplacementGateResponse): CallOffReplacementAdvice => callOffReplacementAdvice(data.outcome, data.basis);

/** How long a gate answer is reused — the same for one shift and for a batch, so both read one cache entry. */
const GATE_STALE_TIME_MS = 60_000;

const gateQueryKey = (departmentId: string, shiftId: string) => ["callOffReplacementGate", departmentId, shiftId] as const;

const fetchGate = async (departmentId: string, shiftId: string) => (await backendService.getCallOffReplacementGate({ departmentId, shiftId })).data;

// Mode-aware replacement nudge for the call-off dialog: the backend gate (HPPD floor for HPPD depts, ratio ladder
// for ratio ones) is the source of truth — the FE no longer re-derives it HPPD-only.
export const useCallOffReplacementGate = ({ departmentId, shiftId, enabled }: { departmentId: string; shiftId: string; enabled: boolean }) => {
	const { data, error, isError, isFetching } = useQuery({
		queryKey: gateQueryKey(departmentId, shiftId),
		queryFn: () => fetchGate(departmentId, shiftId),
		select: toAdvice,
		enabled,
		staleTime: GATE_STALE_TIME_MS,
		throwOnError: false,
	});

	const isGateReady = enabled && !isError && data !== undefined;
	return {
		// Null until the gate is ready: a failed preview is "unknown", never a decision — even when an earlier
		// successful answer is still cached.
		advice: isGateReady ? data : null,
		isGateReady,
		isGateLoading: enabled && isFetching,
		isGateError: enabled && isError,
		gateError: error,
	};
};

/** One shift's gate read, as the batch summary consumes it. */
export type ReplacementGateRead = { advice: CallOffReplacementAdvice | undefined; isError: boolean; error: unknown };

/** What a batch of call-offs recommends, from every shift's gate. */
export type ReplacementGatesSummary = {
	/** Every gate answered (none loading, none failed) — the "search" choice waits for this. */
	isReady: boolean;
	/** Search when any shift recommends it, skip only when every shift does, otherwise none (nothing promoted). */
	recommendation: ReplacementRecommendation | null;
	/** The note every shift's advice agrees on — null when they differ, so no note speaks for a shift it doesn't fit. */
	message: CallOffReplacementHint | null;
	/** The first failed gate's error, surfaced once for the whole batch. */
	error: unknown;
	isError: boolean;
};

const sameHint = (a: CallOffReplacementHint | null, b: CallOffReplacementHint | null): boolean => a?.messageKey === b?.messageKey;

/**
 * Folds N gates' advice into the batch's. One shift that needs cover is enough to recommend searching; skipping
 * needs every shift to agree, and a shift whose coverage couldn't be checked recommends neither. Until every gate
 * has answered there is no recommendation — an empty batch included, since there is nothing to decide.
 */
export const summarizeReplacementGates = (reads: readonly ReplacementGateRead[]): ReplacementGatesSummary => {
	const failed = reads.find((read) => read.isError);
	const advices = reads.flatMap((read) => (read.advice ? [read.advice] : []));
	const isReady = reads.length > 0 && !failed && advices.length === reads.length;
	const base = { isReady, error: failed?.error, isError: failed !== undefined };
	if (!isReady) return { ...base, recommendation: null, message: null };
	const [first] = advices;
	const recommendation = advices.some((advice) => advice.recommendation === "search")
		? "search"
		: advices.every((advice) => advice.recommendation === "skip")
			? "skip"
			: null;
	return { ...base, recommendation, message: advices.every((advice) => sameHint(advice.message, first.message)) ? first.message : null };
};

/**
 * The replacement gate for every shift of a multi-remove that will be offered a call-off, read in parallel and
 * summarized for the one "search for replacements?" choice. Shares `useCallOffReplacementGate`'s cache.
 */
export const useCallOffReplacementGates = ({
	departmentId,
	shiftIds,
	enabled,
}: {
	departmentId: string;
	shiftIds: readonly string[];
	enabled: boolean;
}): ReplacementGatesSummary =>
	useQueries({
		queries: shiftIds.map((shiftId) => ({
			queryKey: gateQueryKey(departmentId, shiftId),
			queryFn: () => fetchGate(departmentId, shiftId),
			select: toAdvice,
			enabled,
			staleTime: GATE_STALE_TIME_MS,
			throwOnError: false,
		})),
		combine: (results) =>
			summarizeReplacementGates(
				results.map((result) => ({
					advice: enabled && !result.isError ? result.data : undefined,
					isError: enabled && result.isError,
					error: result.error,
				})),
			),
	});
